import { Timestamp } from "@bufbuild/protobuf";
import { FundSource } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import {
  type ConsumeDistributionCodeRequest,
  ConsumeDistributionCodeResponse,
  type ConsumeStandaloneDistributionCodeRequest,
  ConsumeStandaloneDistributionCodeResponse,
  type GetDistributionCodeDetailRequest,
  GetDistributionCodeDetailResponse,
  type GetStandaloneDistributionCodeDetailRequest,
  GetStandaloneDistributionCodeDetailResponse,
} from "schema/gen/es/chiikipoint/wallet/v2/service_pb";

// モック配布コードのマッピング（コード -> 配布内容）を保持
const mockDistributionCodes = new Map<
  string,
  {
    amount: bigint;
    fundSource: FundSource;
    expiresAt?: { seconds: bigint; nanos: number };
  }
>();

// 初期データを設定
(() => {
  // 30日後の有効期限
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
  thirtyDaysFromNow.setHours(23, 59, 59, 999);
  const expireAtThirtyDays = {
    seconds: BigInt(Math.floor(thirtyDaysFromNow.getTime() / 1000)),
    nanos: 0,
  };

  // テスト用配布コード
  mockDistributionCodes.set("HEALTH2025", {
    amount: BigInt(500),
    fundSource: new FundSource({
      id: "health-event",
      name: "健康イベント参加ポイント",
      priorityWeight: BigInt(5),
      forbidTransfer: false,
    }),
    expiresAt: expireAtThirtyDays,
  });

  mockDistributionCodes.set("SURVEY100", {
    amount: BigInt(100),
    fundSource: new FundSource({
      id: "survey-answer",
      name: "アンケート回答ポイント",
      priorityWeight: BigInt(3),
      forbidTransfer: false,
    }),
    expiresAt: expireAtThirtyDays,
  });

  mockDistributionCodes.set("TEST1000", {
    amount: BigInt(1000),
    fundSource: new FundSource({
      id: "test-distribution",
      name: "テスト配布ポイント",
      priorityWeight: BigInt(1),
      forbidTransfer: false,
    }),
    expiresAt: expireAtThirtyDays,
  });

  // 期限切れのコード（テスト用）
  const expiredDate = new Date();
  expiredDate.setDate(expiredDate.getDate() - 1);
  mockDistributionCodes.set("EXPIRED100", {
    amount: BigInt(100),
    fundSource: new FundSource({
      id: "expired-campaign",
      name: "期限切れキャンペーン",
      priorityWeight: BigInt(10),
      forbidTransfer: false,
    }),
    expiresAt: {
      seconds: BigInt(Math.floor(expiredDate.getTime() / 1000)),
      nanos: 0,
    },
  });
})();

/**
 * ConsumeDistributionCode RPCのモックハンドラー
 * 配布コードを消費して、対応するポイントを付与します
 */
export async function handleConsumeDistributionCode(
  req: ConsumeDistributionCodeRequest,
) {
  // 通信遅延をシミュレート
  await new Promise((resolve) => setTimeout(resolve, 500));

  // 配布コードが存在するかチェック
  const distribution = mockDistributionCodes.get(req.distributionCode);

  if (!distribution) {
    // コードが見つからない場合はエラーをスロー
    throw new Error("Invalid distribution code");
  }

  // 成功レスポンスを返す
  return new ConsumeDistributionCodeResponse({
    amount: distribution.amount,
    fundSource: distribution.fundSource,
    expiresAt: distribution.expiresAt,
  });
}

/**
 * GetDistributionCodeDetail RPCのモックハンドラー
 * 配布コードの詳細を取得します（消費せずに確認のみ）
 */
export async function handleGetDistributionCodeDetail(
  req: GetDistributionCodeDetailRequest,
) {
  // 通信遅延をシミュレート
  await new Promise((resolve) => setTimeout(resolve, 300));

  // 配布コードが存在するかチェック
  const distribution = mockDistributionCodes.get(req.distributionCode);

  if (!distribution) {
    // コードが見つからない場合はエラーをスロー
    throw new Error("Invalid distribution code");
  }

  // 詳細レスポンスを返す
  return new GetDistributionCodeDetailResponse({
    amount: distribution.amount,
    fundSource: distribution.fundSource,
    expiresAt: distribution.expiresAt,
  });
}

// スタンドアロン配布コードのモックデータ
type StandaloneDistributionCodeData = {
  amount: bigint;
  fundSource: FundSource;
  balanceExpiresAt: Timestamp;
  codeExpiresAt?: Timestamp;
  consumed: boolean;
};

const mockStandaloneDistributionCodes = new Map<
  string,
  StandaloneDistributionCodeData
>();

// 初期データを設定
(() => {
  // 30日後の有効期限
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
  thirtyDaysFromNow.setHours(23, 59, 59, 999);
  const balanceExpiresAtThirtyDays = Timestamp.fromDate(thirtyDaysFromNow);

  // 60日後のコード有効期限
  const sixtyDaysFromNow = new Date();
  sixtyDaysFromNow.setDate(sixtyDaysFromNow.getDate() + 60);
  const codeExpiresAtSixtyDays = Timestamp.fromDate(sixtyDaysFromNow);

  // テスト用スタンドアロン配布コード
  mockStandaloneDistributionCodes.set("SD001", {
    amount: BigInt(1000),
    fundSource: new FundSource({
      id: "standalone-distribution-1",
      name: "スタンドアロン配布ポイント",
      priorityWeight: BigInt(1),
      forbidTransfer: false,
    }),
    balanceExpiresAt: balanceExpiresAtThirtyDays,
    codeExpiresAt: codeExpiresAtSixtyDays,
    consumed: false,
  });

  mockStandaloneDistributionCodes.set("SD002", {
    amount: BigInt(500),
    fundSource: new FundSource({
      id: "standalone-distribution-2",
      name: "イベント参加特典",
      priorityWeight: BigInt(2),
      forbidTransfer: false,
    }),
    balanceExpiresAt: balanceExpiresAtThirtyDays,
    consumed: false,
  });

  mockStandaloneDistributionCodes.set("SD003", {
    amount: BigInt(2000),
    fundSource: new FundSource({
      id: "standalone-distribution-3",
      name: "特別配布ポイント",
      priorityWeight: BigInt(1),
      forbidTransfer: false,
    }),
    balanceExpiresAt: balanceExpiresAtThirtyDays,
    codeExpiresAt: codeExpiresAtSixtyDays,
    consumed: false,
  });

  // 使用済みコード（テスト用）
  mockStandaloneDistributionCodes.set("SD999", {
    amount: BigInt(100),
    fundSource: new FundSource({
      id: "standalone-distribution-consumed",
      name: "使用済み配布ポイント",
      priorityWeight: BigInt(10),
      forbidTransfer: false,
    }),
    balanceExpiresAt: balanceExpiresAtThirtyDays,
    consumed: true,
  });
})();

/**
 * GetStandaloneDistributionCodeDetail RPCのモックハンドラー
 * スタンドアロン配布コードの詳細を取得します（消費せずに確認のみ）
 */
export async function handleGetStandaloneDistributionCodeDetail(
  req: GetStandaloneDistributionCodeDetailRequest,
) {
  // 通信遅延をシミュレート
  await new Promise((resolve) => setTimeout(resolve, 300));

  // 配布コードが存在するかチェック
  const distribution = mockStandaloneDistributionCodes.get(
    req.distributionCode,
  );

  if (!distribution) {
    // コードが見つからない場合はエラーをスロー
    throw new Error("Invalid standalone distribution code");
  }

  // 使用済みの場合はエラー
  if (distribution.consumed) {
    throw new Error("[failed_precondition] code already consumed");
  }

  // 詳細レスポンスを返す
  return new GetStandaloneDistributionCodeDetailResponse({
    amount: distribution.amount,
    fundSource: distribution.fundSource,
    balanceExpiresAt: distribution.balanceExpiresAt,
    codeExpiresAt: distribution.codeExpiresAt,
  });
}

/**
 * ConsumeStandaloneDistributionCode RPCのモックハンドラー
 * スタンドアロン配布コードを消費して、対応するポイントを付与します
 */
export async function handleConsumeStandaloneDistributionCode(
  req: ConsumeStandaloneDistributionCodeRequest,
) {
  // 通信遅延をシミュレート
  await new Promise((resolve) => setTimeout(resolve, 500));

  // 配布コードが存在するかチェック
  const distribution = mockStandaloneDistributionCodes.get(
    req.distributionCode,
  );

  if (!distribution) {
    // コードが見つからない場合はエラーをスロー
    throw new Error("Invalid standalone distribution code");
  }

  // 使用済みの場合はエラー
  if (distribution.consumed) {
    throw new Error("[failed_precondition] code already consumed");
  }

  // コードを使用済みにする
  distribution.consumed = true;

  // 成功レスポンスを返す
  return new ConsumeStandaloneDistributionCodeResponse({
    amount: distribution.amount,
    fundSource: distribution.fundSource,
    balanceExpiresAt: distribution.balanceExpiresAt,
    codeExpiresAt: distribution.codeExpiresAt,
  });
}
